import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { format, formatDistanceToNow } from 'date-fns';
import {
  ArrowLeft,
  Package,
  Clock,
  DollarSign,
  Users,
  Calendar,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useHourlyPackage } from '../hooks';
import { HourlyPackageAction } from '../components';
import { useAuthStore } from '@/features/auth/stores/auth.store';

function InfoField({
  icon: Icon,
  label,
  value,
}: {
  icon?: React.ComponentType<{ className?: string }>;
  label: string;
  value: string | React.ReactNode;
}) {
  return (
    <div className="flex items-start gap-3">
      {Icon && <Icon className="h-4 w-4 text-slate-400 mt-0.5 shrink-0" />}
      <div className="min-w-0 flex-1">
        <p className="text-xs font-medium uppercase tracking-wider text-slate-500 mb-1">
          {label}
        </p>
        <p className="text-sm font-medium text-slate-900">
          {value || '-'}
        </p>
      </div>
    </div>
  );
}

export function HourlyPackageDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const hasAccess = useAuthStore((state) => state.hasAccess);
  const { data: pkg, isLoading, isError, error } = useHourlyPackage(id);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <div className="bg-white border-b border-slate-200 px-6 py-8">
          <div className="max-w-6xl mx-auto">
            <Skeleton className="h-4 w-24 mb-6" />
            <div className="flex items-center gap-6">
              <Skeleton className="h-16 w-16 rounded-full" />
              <div className="space-y-3">
                <Skeleton className="h-10 w-48" />
                <Skeleton className="h-6 w-24" />
              </div>
            </div>
          </div>
        </div>
        <div className="max-w-6xl mx-auto px-6 py-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <Skeleton className="h-48" />
            <Skeleton className="h-48" />
            <Skeleton className="h-48" />
          </div>
        </div>
      </div>
    );
  }

  if (isError || !pkg) {
    return (
      <div className="min-h-screen bg-slate-50 p-6">
        <div className="max-w-6xl mx-auto">
          <div className="bg-white rounded-lg border border-slate-200 p-8 text-center">
            <div className="text-red-500 mb-4">
              <Package className="h-12 w-12 mx-auto mb-2 opacity-50" />
              <p className="text-lg font-medium">
                {(error as Error)?.message || t('failedToLoadHourlyPackage')}
              </p>
            </div>
            <Link to="/app/hourly-packages">
              <Button variant="outline">
                <ArrowLeft className="mr-2 h-4 w-4" />
                {t('backToHourlyPackages')}
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Hero Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-6 py-6">
          <Link
            to="/app/hourly-packages"
            className="inline-flex items-center text-sm font-medium text-slate-600 hover:text-slate-900 mb-6 transition-colors"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t('backToHourlyPackages')}
          </Link>

          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
            <div className="flex items-center gap-5">
              <div className="h-16 w-16 rounded-full flex items-center justify-center border-4 border-white shadow-lg bg-blue-100">
                <Package className="h-8 w-8 text-blue-600" />
              </div>
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-2">
                  {pkg.name}
                </h1>
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-slate-100 border border-slate-300 text-xs font-medium uppercase tracking-wide text-slate-700">
                  #{String(pkg.id).padStart(5, '0')}
                </span>
              </div>
            </div>
            {hasAccess('hourly-package', 'update') && (
              <HourlyPackageAction id={pkg.id} />
            )}
          </div>
        </div>
      </div>

      {/* Info Cards */}
      <div className="max-w-6xl mx-auto px-6 py-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Package Details */}
          <Card className="bg-white border-slate-200 shadow-sm hover:shadow-md transition-shadow">
            <CardHeader className="pb-3 border-b border-slate-100">
              <CardTitle className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                <Package className="h-5 w-5 text-slate-400" />
                {t('packageInformation')}
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-4 space-y-4">
              <InfoField icon={Clock} label={t('hours')} value={String(pkg.hours)} />
              <InfoField icon={DollarSign} label={t('price')} value={pkg.price?.toLocaleString()} />
              <InfoField icon={DollarSign} label={t('overTimeHourlyCost')} value={pkg.overTimeHourlyCost?.toLocaleString()} />
            </CardContent>
          </Card>

          {/* System Info */}
          <Card className="bg-white border-slate-200 shadow-sm hover:shadow-md transition-shadow">
            <CardHeader className="pb-3 border-b border-slate-100">
              <CardTitle className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                <Calendar className="h-5 w-5 text-slate-400" />
                {t('systemInfo')}
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-4 space-y-4">
              <InfoField label={t('id')} value={String(pkg.id)} />
              <InfoField
                label={t('createdAt')}
                value={pkg.createdAt ? format(new Date(pkg.createdAt), 'MMM d, yyyy HH:mm') : '-'}
              />
              <InfoField
                label={t('updatedAt')}
                value={
                  pkg.updatedAt ? (
                    <span>
                      {format(new Date(pkg.updatedAt), 'MMM d, yyyy HH:mm')}
                      <span className="text-slate-400 text-xs ml-2">
                        ({formatDistanceToNow(new Date(pkg.updatedAt), { addSuffix: true })})
                      </span>
                    </span>
                  ) : '-'
                }
              />
            </CardContent>
          </Card>

          {/* Rates Table */}
          {/*
          <Card className="bg-white border-slate-200 shadow-sm hover:shadow-md transition-shadow md:col-span-2 lg:col-span-3">
            <CardHeader className="pb-3 border-b border-slate-100">
              <CardTitle className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                <Users className="h-5 w-5 text-slate-400" />
                {t('rates')}
                {pkg.rates && pkg.rates.length > 0 && (
                  <span className="ml-2 bg-blue-100 text-blue-700 text-xs font-semibold px-2 py-0.5 rounded-full">
                    {pkg.rates.length}
                  </span>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-4">
              {pkg.rates && pkg.rates.length > 0 ? (
                <div className="rounded-lg border border-[#e2e8f0] overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-[#f8fafc] hover:bg-[#f8fafc]">
                        <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-[#64748b]">#</TableHead>
                        <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-[#64748b]">{t('role')}</TableHead>
                        <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-[#64748b]">{t('rate')}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {pkg.rates.map((rate, index) => (
                        <TableRow key={rate.id} className="hover:bg-[#f8fafc]">
                          <TableCell>{index + 1}</TableCell>
                          <TableCell>{rate.role?.name ?? '-'}</TableCell>
                          <TableCell>{rate.rate?.toLocaleString()}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <Users className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                  <p className="font-medium">{t('noRatesConfigured')}</p>
                </div>
              )}
            </CardContent>
          </Card>
          */}
        </div>
      </div>
    </div>
  );
}
